import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      setStatus('success');
      setIsSubmitting(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setStatus(null), 5000);
    }, 1500);
  };

  return (
    <section id="contact" className="relative py-20 lg:py-28 bg-[#F1F4F6]">
      {/* Background Accents */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-bl from-[#BBEFF3]/40 to-transparent rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B2E4B] mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-[#0B2E4B]/70 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[#0B2E4B] text-sm font-semibold mb-2">
                  {t.contact.form.name}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#F1F4F6] border border-gray-200 rounded-lg text-[#0B2E4B] placeholder-[#0B2E4B]/40 focus:border-[#28BBC5] focus:ring-2 focus:ring-[#28BBC5]/20 focus:outline-none transition-all duration-300"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-[#0B2E4B] text-sm font-semibold mb-2">
                  {t.contact.form.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#F1F4F6] border border-gray-200 rounded-lg text-[#0B2E4B] placeholder-[#0B2E4B]/40 focus:border-[#28BBC5] focus:ring-2 focus:ring-[#28BBC5]/20 focus:outline-none transition-all duration-300"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-[#0B2E4B] text-sm font-semibold mb-2">
                  {t.contact.form.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#F1F4F6] border border-gray-200 rounded-lg text-[#0B2E4B] placeholder-[#0B2E4B]/40 focus:border-[#28BBC5] focus:ring-2 focus:ring-[#28BBC5]/20 focus:outline-none transition-all duration-300"
                  placeholder="787-000-0000"
                />
              </div>

              <div>
                <label className="block text-[#0B2E4B] text-sm font-semibold mb-2">
                  {t.contact.form.message}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-[#F1F4F6] border border-gray-200 rounded-lg text-[#0B2E4B] placeholder-[#0B2E4B]/40 focus:border-[#28BBC5] focus:ring-2 focus:ring-[#28BBC5]/20 focus:outline-none transition-all duration-300 resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              {status === 'success' && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t.contact.form.success}</span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span>{t.contact.form.error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="group flex items-center justify-center gap-3 w-full px-8 py-4 bg-[#28BBC5] text-white font-semibold text-lg rounded-lg hover:bg-[#22a5ae] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#28BBC5]/30"
              >
                {isSubmitting ? (
                  <span className="animate-pulse">Sending...</span>
                ) : (
                  <>
                    {t.contact.form.submit}
                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col justify-center">
            <div className="space-y-8">
              <div className="flex items-start gap-5">
                <div className="flex items-center justify-center w-14 h-14 bg-[#28BBC5] text-white rounded-xl shadow-lg">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-[#0B2E4B] font-semibold text-lg mb-1">Location</h4>
                  <p className="text-[#0B2E4B]/60">{t.contact.info.location}</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="flex items-center justify-center w-14 h-14 bg-[#28BBC5] text-white rounded-xl shadow-lg">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-[#0B2E4B] font-semibold text-lg mb-1">Phone</h4>
                  <a 
                    href={`tel:${t.contact.info.phone}`}
                    className="text-[#0B2E4B]/60 hover:text-[#28BBC5] transition-colors duration-300"
                  >
                    {t.contact.info.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="flex items-center justify-center w-14 h-14 bg-[#28BBC5] text-white rounded-xl shadow-lg">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-[#0B2E4B] font-semibold text-lg mb-1">Email</h4>
                  <a 
                    href={`mailto:${t.contact.info.email}`}
                    className="text-[#0B2E4B]/60 hover:text-[#28BBC5] transition-colors duration-300"
                  >
                    {t.contact.info.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Quote Box */}
            <div className="mt-12 p-6 bg-gradient-to-br from-[#0B2E4B] to-[#0d3a5c] rounded-2xl text-white">
              <p className="text-xl italic mb-3">
                "Powering Your Digital Vision"
              </p>
              <p className="text-[#28BBC5] font-semibold">- BrightCore PR</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
